import React, { FunctionComponent, useMemo, useState } from "react";
import { Theme } from "@emotion/react";
import { UseIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { styled } from "@iventis/styles";
import { useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Node } from "@iventis/domain-model/model/node";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { Content } from "@iventis/translations";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { FormWizardTitle } from "./form-wizard-title";
import { FormWizardTemplate, Stage } from "./form-wizard-template";
import { SwitchWithLabel } from "./custom-switch";
import { CustomDialog } from "./custom-dialog";

export type NodeWithMapNode = Node & { mapName: string; mapId: string };

const HelpbaseBlurb = ({ translate }) => (
    <p>
        {translate(Content.map6.copyOrUnlinkLayers.helpbase_blurb.click)}{" "}
        <StyledA target="_blank" rel="noreferrer" href="https://support.iventis.com/getting-started">
            {translate(Content.map6.copyOrUnlinkLayers.helpbase_blurb.link)}
        </StyledA>{" "}
        {translate(Content.map6.copyOrUnlinkLayers.helpbase_blurb.further_info)}
    </p>
);

export const UnlinkOrCopyLayerDialog: FunctionComponent<{
    onClose?: () => void;
    onConfirm: (includeMapObjects: boolean, keepPermissions: boolean) => void;
    open: boolean;
    translate?: UseIventisTranslate;
    stage?: number;
    hasNoMapObjects: boolean;
    nodesSelected: Node[];
    selectedLayers: MapLayer[];
    getImportedLayerMapNames: (nodes: Node[]) => Promise<NodeWithMapNode[]>;
}> = ({ onClose, onConfirm, open, stage, translate, hasNoMapObjects = false, getImportedLayerMapNames, nodesSelected, selectedLayers }) => {
    const [includeMapObjects, setIncludeMapObjects] = useState(true);

    const [keepPermissions, setKeepPermissions] = useState(false);

    const theme = useTheme<Theme>();

    const layerNodes = useMemo(() => nodesSelected.filter(({ id }) => selectedLayers.some(({ id: layerId }) => id === layerId)), [nodesSelected, selectedLayers]);

    const { data: nodes } = useQuery(
        layerNodes.map((node) => node.id),
        () => getImportedLayerMapNames(layerNodes)
    );
    const firstNode = nodes?.find((n) => n !== undefined);
    const singleMapName = firstNode?.mapName;
    const singleMapId = firstNode?.mapId;

    const multipleLayersSelected = selectedLayers.length > 1;
    const hasImportedLayersAndCopying = multipleLayersSelected && nodes?.length;

    const nodeTypes = nodesSelected.map((node) => node.type);
    const layerTypesCount = nodeTypes.filter((nt) => nt === NodeType.Layer).length;
    const groupTypesCount = nodeTypes.filter((nt) => nt === NodeType.Folder).length;

    const copyBlurbText = () => {
        if (layerTypesCount > 0 && groupTypesCount > 0) {
            return translate(Content.map6.copyOrUnlinkLayers.copy_blurb.layers_and_groups);
        }
        if (layerTypesCount > 0) {
            return translate(Content.map6.copyOrUnlinkLayers.copy_blurb.layer, {
                pluralise: layerTypesCount > 1 ? "s" : "",
            });
        }
        return translate(Content.map6.copyOrUnlinkLayers.copy_blurb.group, {
            pluralise: groupTypesCount > 1 ? "s" : "",
        });
    };

    const stages: Stage[] = [
        {
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: onClose }],
            primaryButtonText: translate(Content.map6.copyOrUnlinkLayers.copy),
            primaryButtonCallback: () => onConfirm(includeMapObjects, keepPermissions),
            isValid: true,
            Component: (
                <StyledContainer data-testid="copy-blurb">
                    {hasImportedLayersAndCopying ? (
                        <p>{translate(Content.map6.copyOrUnlinkLayers.unlink_blurb)}</p>
                    ) : (
                        <p>
                            <span>{translate(Content.map6.copyOrUnlinkLayers.copy_blurb.are_you_sure)} </span>
                            {nodeTypes.length > 1 ? (
                                <span>
                                    {translate(Content.map6.copyOrUnlinkLayers.copy_blurb.these)} {copyBlurbText()}
                                </span>
                            ) : (
                                <span>
                                    {translate(Content.map6.copyOrUnlinkLayers.copy_blurb.this)} {copyBlurbText()}
                                </span>
                            )}
                            ?
                        </p>
                    )}

                    <HelpbaseBlurb translate={translate} />

                    <StyledSwitchWithLabel
                        checked={includeMapObjects}
                        onChange={() => setIncludeMapObjects(!includeMapObjects)}
                        label={translate(Content.map6.copyOrUnlinkLayers.copy_with_map_objects, { pluralise: multipleLayersSelected ? "s" : "" })}
                        theme={theme}
                        disabled={hasNoMapObjects}
                    />

                    <StyledSwitchWithLabel
                        checked={keepPermissions}
                        onChange={() => setKeepPermissions((val) => !val)}
                        label={translate(Content.permissions.copyPermissions)}
                        theme={theme}
                        dataCy="copy-permissions"
                    />

                    {!!hasImportedLayersAndCopying && (
                        <>
                            <StyledHr />
                            <StyledTable>
                                <tr>
                                    <th>{translate(Content.map6.copyOrUnlinkLayers.linked_layers)}</th>
                                    <th>{translate(Content.map6.copyOrUnlinkLayers.original_plan)}</th>
                                </tr>
                                {nodes.map((node) => (
                                    <tr key={node.id}>
                                        <td>{node?.name}</td>
                                        <td>
                                            <StyledA target="_blank" rel="noreferrer" href={`/spatial-planner/map/${node?.mapId}`}>
                                                {node?.mapName}
                                            </StyledA>
                                        </td>
                                    </tr>
                                ))}
                            </StyledTable>
                        </>
                    )}
                </StyledContainer>
            ),
        },
        {
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.cancel), onButtonPressed: onClose }],
            primaryButtonText: translate(Content.map6.copyOrUnlinkLayers.unlink),
            primaryButtonCallback: () => onConfirm(includeMapObjects, keepPermissions),
            isValid: true,
            Component: (
                <StyledContainer>
                    <p>
                        {translate(Content.map6.copyOrUnlinkLayers.unlink_info.links_to)}{" "}
                        <StyledA target="_blank" rel="noreferrer" href={`/spatial-planner/map/${singleMapId}`}>
                            {singleMapName}
                        </StyledA>
                        . {translate(Content.map6.copyOrUnlinkLayers.unlink_info.changes_not_reflected)}
                    </p>
                    <HelpbaseBlurb translate={translate} />
                    <StyledSwitchWithLabel
                        checked={includeMapObjects}
                        onChange={() => setIncludeMapObjects(!includeMapObjects)}
                        label={translate(Content.map6.copyOrUnlinkLayers.copy_with_map_objects, { pluralise: multipleLayersSelected ? "s" : "" })}
                        theme={theme}
                        disabled={hasNoMapObjects}
                    />
                    <StyledSwitchWithLabel
                        checked={keepPermissions}
                        onChange={() => setKeepPermissions((val) => !val)}
                        label={translate(Content.permissions.copyPermissions)}
                        theme={theme}
                        dataCy="copy-permissions"
                    />
                </StyledContainer>
            ),
        },
    ];
    return (
        <CustomDialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
            <FormWizardTemplate
                modalTestId="copy-layers-modal"
                title={
                    (stage === 0 && multipleLayersSelected && (
                        <FormWizardTitle
                            title={
                                <StyledTitle>
                                    <FontAwesomeIcon icon="copy" />
                                    {translate(Content.map6.copyOrUnlinkLayers.copy)}
                                </StyledTitle>
                            }
                        />
                    )) ||
                    (stage === 0 && (
                        <FormWizardTitle
                            title={
                                <StyledTitle>
                                    <FontAwesomeIcon icon="copy" />
                                    {translate(Content.map6.copyOrUnlinkLayers.copy)}
                                </StyledTitle>
                            }
                        />
                    )) ||
                    (stage === 1 && (
                        <FormWizardTitle
                            title={
                                <StyledTitle>
                                    <FontAwesomeIcon icon={["far", "link-slash"]} />
                                    {translate(Content.map6.copyOrUnlinkLayers.unlink_layer)}
                                </StyledTitle>
                            }
                        />
                    ))
                }
                currentStage={stage}
                stages={stages}
            />
        </CustomDialog>
    );
};

const StyledSwitchWithLabel = styled(SwitchWithLabel)`
    display: flex;
    justify-content: space-between;
    margin-left: 0;
    width: calc(100% - 8px);

    .MuiFormControlLabel-label {
        font-weight: bold;
    }
`;

const StyledContainer = styled.div`
    > p {
        margin-bottom: 16px;
    }
`;

const StyledTable = styled.table`
    width: 100%;
    text-align: left;
`;

const StyledHr = styled.hr`
    margin: 10px 0;
    border: none;
    height: 1px;
    background-color: ${(props: { theme: Theme }) => props.theme.shades.lightBorder};
`;

const StyledTitle = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-start;
    align-items: center;
`;

const StyledA = styled.a`
    color: ${(props: { theme: Theme }) => props.theme.primaryColors.focus};
`;
